<!-- eslint-disable vue/no-mutating-props -->
<script>
import { mapGetters } from 'vuex';

import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { DATE_RANGE_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants';
import customDashboardsMixin from 'shared/mixins/customDashboardsMixin';

import { getInboxIdentifier } from 'dashboard/helper/inbox';
import InboxDropdownItem from 'dashboard/components/widgets/InboxDropdownItem.vue';

import Checkbox from './Checkbox.vue';

import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { STATIC_TEXT_WIDGET_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';

import FileUpload from 'vue-upload-component';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';

export default {
  components: {
    WootMessageEditor,
    WootDateRangePicker,
    InboxDropdownItem,
    Checkbox,
    FileUpload,
    AttachmentPreview,
  },
  mixins: [customDashboardsMixin, fileUploadMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    widgetBeingEdited: {
      type: Object,
      default: () => ({}),
    },
    hasStaticImageWidgetOnDashboard: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const translatedOptions = this.translatedDateRangeOptions();

    return {
      showWidgetEditModal: false,

      selectedDisplayForWidget: {},
      dateRangeOptions: translatedOptions,
      selectedDateRange: translatedOptions[0],
      customDateRange: [new Date(), new Date()],

      selectedInbox: null,

      showMetricNameLabelInWidget: false,
      showDateRangeLabelInWidget: false,
      showInboxLabelInWidget: false,
      showSubmetricLabelInWidget: false,

      // MESSAGE WIDGET
      showDetailedMsgsColChart: false, // Show Detailed Count Messages When Column Chart (incoming and outgoing)
      // CONVERSATION WIDGET
      selectedSubmetricForConversation: null,
      // CONVERSATION RESOLUTION REASON WIDGET
      selectedSubmetricForConversationResolutionReason: null,
      selectedMultipleSubmetricsForConversationResolutionReason: [],
      // STATIC WIDGET -> TEXT
      messageForStaticTextDisplay: '',
      customEditorMenuOptions: STATIC_TEXT_WIDGET_EDITOR_MENU_OPTIONS,
      // STATIC WIDGET -> IMAGE
      attachedFiles: [],
      currentFileExtension: '.png/.jpeg',
      imageLinkForStaticImageDisplay: '',
    };
  },
  validations: {},
  computed: {
    ...mapGetters({
      inboxes: 'inboxes/getInboxes',
      globalConfig: 'globalConfig/get',
    }),
    isDateRangeSelected() {
      return (
        this.selectedDateRange.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    to() {
      if (this.isDateRangeSelected) {
        return getUnixEndOfDay(this.customDateRange[1]);
      }
      return getUnixEndOfDay(new Date());
    },
    from() {
      if (this.isDateRangeSelected) {
        return getUnixStartOfDay(this.customDateRange[0]);
      }

      const { offset } = this.selectedDateRange;
      const fromDate = subDays(new Date(), offset);
      return getUnixStartOfDay(fromDate);
    },
    currentWidgetType() {
      return this.widgetOptionsList.find(
        e => e.type === this.widgetBeingEdited.type
      );
    },
    modalDescription() {
      return (
        this.$t('CUSTOM_DASHBOARDS.EDIT_WIDGET.EDITING_METRIC_DESC') +
        ' ' +
        this.currentWidgetType.title
      );
    },
    canFilterByInbox() {
      return this.currentWidgetType.possibleFilters.includes('inbox');
    },
    shouldShowOptionOfDetailedMsgsColChart() {
      return (
        this.widgetBeingEdited.type === 'messages' &&
        this.selectedDisplayForWidget.type === 'column_chart'
      );
    },
    shouldShowSubmetricCheckbox() {
      let widgetTypeWithSubmetrics = [
        'conversations',
        'conversationResolutionReason',
      ];
      return widgetTypeWithSubmetrics.includes(this.widgetBeingEdited.type);
    },
    shouldShowDescriptionsOptions() {
      return !this.IsStaticWidgetBeingEdited;
    },
    shouldShowDateRangeMultiselect() {
      return !this.IsStaticWidgetBeingEdited;
    },
    shouldShowTextEditor() {
      return this.IsStaticTextDisplaySelected;
    },
    shouldShowFileUploadComponent() {
      return this.IsStaticImageDisplaySelected;
    },
    // CONVERSATION WIDGET
    shouldShowConversationSubmetricSelector() {
      return this.widgetBeingEdited.type === 'conversations';
    },
    shouldShowConversationResolutionReasonSubmetricSelector() {
      return this.widgetBeingEdited.type === 'conversationResolutionReason';
    },
    // Conversations Resolutions Reasons
    checkSelectedDisplayForWidget() {
      return this.selectedDisplayForWidget.type;
    },
    checkInboxSelectedIfTableDisplay() {
      if (this.checkSelectedDisplayForWidget === 'table') {
        return !this.selectedInbox;
      }
      return false;
    },
    checkIfDonutDisplayIsSelected() {
      return this.checkSelectedDisplayForWidget === 'donut';
    },
    returnSubmetricsSelecteds: {
      get() {
        if (this.checkIfDonutDisplayIsSelected) {
          return this.selectedMultipleSubmetricsForConversationResolutionReason;
        }
        return this.selectedSubmetricForConversationResolutionReason;
      },
      set(newValue) {
        if (this.checkIfDonutDisplayIsSelected) {
          this.selectedSubmetricForConversationResolutionReason = newValue[0];
          this.selectedMultipleSubmetricsForConversationResolutionReason =
            newValue;
        } else {
          this.selectedSubmetricForConversationResolutionReason = newValue;
        }
      },
    },
    IsStaticWidgetBeingEdited() {
      return this.widgetBeingEdited.type === 'static';
    },
    IsStaticTextDisplaySelected() {
      return this.checkSelectedDisplayForWidget === 'static_text';
    },
    IsStaticImageDisplaySelected() {
      return this.checkSelectedDisplayForWidget === 'static_image';
    },
    allowedFileTypes() {
      return 'image/png, image/jpeg';
    },
    uploadedFileIsNotInCorrectExtension() {
      const fileType = String(this.attachedFiles[0]?.resource?.file?.type);
      const correctExt = this.allowedFileTypes?.includes(fileType.trim());
      return !!this.attachedFiles.length && !correctExt;
    },
    reachedFileLimit() {
      return this.attachedFiles.length >= 1;
    },
    shouldDisableEditButton() {
      return (
        this.uploadedFileIsNotInCorrectExtension ||
        this.checkInboxSelectedIfTableDisplay
      );
    },
  },
  mounted() {
    this.setInitialData();
  },
  methods: {
    translatedDateRangeOptions() {
      return Object.values(DATE_RANGE_OPTIONS).map(option => ({
        ...option,
        name: this.$t(option.translationKey),
      }));
    },
    setInitialData() {
      const {
        display,
        dateRange,
        inbox,
        widgetVisualizationOptions,
        submetric,
        multipleSubmetrics,
        staticTextContent,
      } = this.widgetBeingEdited.contentProps;
      // Setting display
      if (this.currentDisplaysList.length === 1) {
        this.selectedDisplayForWidget = this.currentDisplaysList[0];
      } else {
        this.selectedDisplayForWidget = this.displaysList.find(
          e => e.type === display.type
        );
      }
      // Setting date options
      const { type, range } = dateRange;
      this.selectedDateRange = this.translatedDateRangeOptions().find(
        e => e.id === type
      );
      if (type === 'CUSTOM_DATE_RANGE')
        this.customDateRange = [
          new Date(range[0] * 1000),
          new Date(range[1] * 1000),
        ];
      // Setting inbox
      if (inbox?.id)
        this.selectedInbox = this.inboxes.find(e => e.id === inbox.id);

      this.showMetricNameLabelInWidget = !!widgetVisualizationOptions?.metric;
      this.showInboxLabelInWidget = !!widgetVisualizationOptions?.inbox;
      this.showDateRangeLabelInWidget = !!widgetVisualizationOptions?.dateRange;
      this.showDetailedMsgsColChart =
        !!widgetVisualizationOptions?.showDetailedMsgsColChart;
      this.showSubmetricLabelInWidget = !!widgetVisualizationOptions?.submetric;

      this.selectedSubmetricForConversation = submetric?.type
        ? this.conversationSubmetricsList.find(e => e.type === submetric.type)
        : null;

      this.selectedSubmetricForConversationResolutionReason = submetric?.type
        ? this.conversationResolutionReasonsSubmetricsList.find(
            e => e.type === submetric.type
          )
        : null;

      if (multipleSubmetrics) {
        this.conversationResolutionReasonsSubmetricsList.forEach(e => {
          const typesArray = multipleSubmetrics.map(m => m.type);
          if (typesArray.includes(e.type))
            this.selectedMultipleSubmetricsForConversationResolutionReason.push(
              e
            );
        });
      }

      this.messageForStaticTextDisplay = staticTextContent;
    },

    closeModal() {
      this.$emit('closeModal');
    },
    editCurrentWidget() {
      const widgetSettings = {
        display: { type: this.selectedDisplayForWidget.type },
        inbox: { id: this.selectedInbox?.id },
        submetric:
          this.selectedSubmetricForConversation ||
          this.selectedSubmetricForConversationResolutionReason,
        multipleSubmetrics:
          this.selectedMultipleSubmetricsForConversationResolutionReason,
        dateRange: {
          type: this.selectedDateRange.id,
          range: this.currentDateRange(),
        },
        widgetVisualizationOptions: {
          metric: this.showMetricNameLabelInWidget,
          inbox: this.showInboxLabelInWidget,
          dateRange: this.showDateRangeLabelInWidget,
          submetric: this.showSubmetricLabelInWidget,
          showDetailedMsgsColChart: this.showDetailedMsgsColChart,
        },
        staticTextContent: this.messageForStaticTextDisplay || '',
        imageLinkContent:
          this.imageLinkForStaticImageDisplay ||
          this.attachedFiles[0]?.thumb ||
          '',
      };

      this.$emit('editCurrentWidget', widgetSettings);
    },

    // Returns period as an array of Unix timestamps, e.g., [1721358000, 1721962799]
    currentDateRange() {
      const { from, to } = this;
      return [from, to];
    },
    onDateRangeChange(selectedRange) {
      this.selectedDateRange = selectedRange;
    },
    onCustomDateRangeChange(value) {
      this.customDateRange = value;
    },

    computedInboxSource(inbox) {
      return getInboxIdentifier(inbox);
    },

    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },

    onFilesUpdated(file) {
      this.onFileUpload(file);
      this.$emit('updateAttachedFiles', this.attachedFiles);
    },

    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <woot-modal-header
      :header-title="
        $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.EDITING_METRIC_TITLE') +
        widgetBeingEdited.headerText
      "
      :header-content="modalDescription"
    />
    <!-- DATE RANGE MULTISELECT -->
    <div v-if="shouldShowDateRangeMultiselect" class="px-8 pb-5">
      <label>{{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.INTERVAL') }}</label>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedDateRange"
          class="no-margin text-xxs"
          track-by="name"
          label="name"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="dateRangeOptions"
          :searchable="false"
          :allow-empty="false"
          :max-height="150"
          @select="onDateRangeChange"
        />
      </div>
      <WootDateRangePicker
        v-if="isDateRangeSelected"
        show-range
        class="no-margin auto-width pt-2"
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateRangeChange"
      />
    </div>
    <!-- INBOX MULTISELECT -->
    <div v-if="canFilterByInbox" class="px-8 pb-5">
      <label> {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.INBOX') }} </label>
      <multiselect
        v-model="selectedInbox"
        track-by="id"
        label="name"
        :placeholder="$t('FORMS.MULTISELECT.SELECT')"
        selected-label=""
        select-label=""
        deselect-label=""
        class="!m-0"
        :max-height="160"
        close-on-select
        :options="[...inboxes]"
      >
        <template #singleLabel="{ option }">
          <InboxDropdownItem
            v-if="option.name"
            :name="option.name"
            :inbox-identifier="computedInboxSource(option)"
            :channel-type="option.channel_type"
          />
          <span v-else>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.PLACEHOLDER') }}
          </span>
        </template>
        <template #option="{ option }">
          <InboxDropdownItem
            :name="option.name"
            :inbox-identifier="computedInboxSource(option)"
            :channel-type="option.channel_type"
          />
        </template>
      </multiselect>
    </div>

    <!-- CONVERSATIONS SUBMETRIC OPTIONS MULTISELECT -->
    <div v-if="shouldShowConversationSubmetricSelector" class="px-8 pb-5">
      <label>{{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.SUBMETRIC') }}</label>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedSubmetricForConversation"
          class="no-margin text-xxs"
          track-by="type"
          label="title"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="conversationSubmetricsList"
          :searchable="false"
          :allow-empty="false"
          :max-height="150"
        />
      </div>
    </div>

    <!-- CONVERSATION RESOLUTION REASONS SUBMETRIC OPTIONS MULTISELECT -->
    <div
      v-if="shouldShowConversationResolutionReasonSubmetricSelector"
      class="px-8 pb-5"
    >
      <label>{{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.SUBMETRIC') }}</label>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="returnSubmetricsSelecteds"
          class="no-margin text-xxs"
          track-by="type"
          label="title"
          selected-label
          :multiple="checkIfDonutDisplayIsSelected"
          :close-on-select="!checkIfDonutDisplayIsSelected"
          :clear-on-select="!checkIfDonutDisplayIsSelected"
          :hide-selected="checkIfDonutDisplayIsSelected"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="conversationResolutionReasonsSubmetricsList"
          :searchable="false"
          :allow-empty="false"
          :max-height="150"
        />
      </div>
    </div>

    <!-- TEXT EDITOR FOR STATIC WIDGET AND STATIC TEXT DISPLAY -->
    <div v-if="shouldShowTextEditor" class="px-8 pb-5">
      <label>{{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.TEXT_EDITOR') }}</label>
      <WootMessageEditor
        v-model="messageForStaticTextDisplay"
        class="message-editor h-[11rem]"
        is-format-mode
        :placeholder="$t('CUSTOM_DASHBOARDS.EDIT_WIDGET.CONTENT_PLACEHOLDER')"
        :enabled-menu-options="customEditorMenuOptions"
        :enable-suggestions="false"
        show-image-resize-toolbar
      />
    </div>

    <!-- IMAGE UPLOADER FOR STATIC WIDGET AND STATIC IMAGE DISPLAY -->
    <div v-if="shouldShowFileUploadComponent" class="px-8 pb-5">
      <label>{{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.IMAGE_UPLOAD') }}</label>
      <FileUpload
        ref="upload"
        input-id="customDashboardAttachment"
        :accept="allowedFileTypes"
        class="w-full"
        :size="4096 * 4096"
        :multiple="false"
        drop
        :drop-directory="false"
        :disabled="reachedFileLimit"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFilesUpdated"
      >
        <woot-button
          v-if="true"
          :title="$t('CUSTOM_DASHBOARDS.EDIT_WIDGET.TIP_ATTACH_ICON')"
          icon="attach"
          emoji="📎"
          color-scheme="secondary"
          variant="smooth"
          class="w-full flex justify-center items-center"
          :style="
            uploadedFileIsNotInCorrectExtension
              ? 'border: 1px solid red'
              : 'border: none'
          "
        >
          {{
            $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.ATTACH') +
            ` ${currentFileExtension}`
          }}
        </woot-button>
        <span
          v-if="uploadedFileIsNotInCorrectExtension"
          class="pl-2 text-red-400 text-xs inline-block"
        >
          {{
            $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.SUBMIT_FILE') +
            ` ${currentFileExtension}`
          }}
        </span>
        <span
          v-else-if="reachedFileLimit"
          class="pl-2 text-red-400 text-xs inline-block"
        >
          {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.REACHED_FILE_LIMIT') }}
        </span>
      </FileUpload>
      <div v-if="attachedFiles.length">
        <AttachmentPreview
          class="flex-col mt-4"
          :attachments="attachedFiles"
          :remove-attachment="removeAttachment"
        />
      </div>
    </div>

    <!-- DISPLAY MULTISELECT -->
    <div class="px-8 pb-5">
      <label>
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS') }}
      </label>
      <div class="multiselect-wrap--small">
        <multiselect
          v-model="selectedDisplayForWidget"
          class="no-margin text-xxs"
          track-by="type"
          label="title"
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          deselect-label=""
          :options="currentDisplaysList"
          :searchable="false"
          :allow-empty="false"
          :max-height="150"
        />
      </div>
      <div
        v-if="shouldShowOptionOfDetailedMsgsColChart"
        @click="showDetailedMsgsColChart = !showDetailedMsgsColChart"
      >
        <Checkbox
          :checked="showDetailedMsgsColChart"
          i18n-key="CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS_LIST.SHOW_DETAILED_MSGS_COL_CHART"
        />
      </div>
    </div>

    <!-- SHOW DESCRIPTIONS IN WIDGET SECTION -->
    <div v-if="shouldShowDescriptionsOptions" class="px-8 pb-5">
      <label>
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.SHOW_DESCRIPTIONS') }}
      </label>
      <!-- SHOW METRIC NAME LABEL IN WIDGET -->
      <div @click="showMetricNameLabelInWidget = !showMetricNameLabelInWidget">
        <Checkbox
          :checked="showMetricNameLabelInWidget"
          i18n-key="CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS_LIST.METRIC"
        />
      </div>
      <!-- SHOW SUB METRIC LABEL IN WIDGET -->
      <div
        v-if="shouldShowSubmetricCheckbox"
        @click="showSubmetricLabelInWidget = !showSubmetricLabelInWidget"
      >
        <Checkbox
          :checked="showSubmetricLabelInWidget"
          i18n-key="CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS_LIST.SUBMETRIC"
        />
      </div>
      <!-- SHOW DATE RANGE LABEL IN WIDGET -->
      <div @click="showDateRangeLabelInWidget = !showDateRangeLabelInWidget">
        <Checkbox
          :checked="showDateRangeLabelInWidget"
          i18n-key="CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS_LIST.DATE_RANGE"
        />
      </div>
      <!-- SHOW INBOX LABEL IN WIDGET -->
      <div
        v-if="selectedInbox"
        @click="showInboxLabelInWidget = !showInboxLabelInWidget"
      >
        <Checkbox
          :checked="showInboxLabelInWidget"
          i18n-key="CUSTOM_DASHBOARDS.EDIT_WIDGET.VISUALIZATION_OPTIONS_LIST.INBOX"
        />
      </div>
    </div>

    <!-- EDIT AND CANCEL MODAL BUTTONS -->
    <div class="flex flex-row justify-end gap-2 py-8 pr-8 w-full">
      <woot-button
        :disabled="shouldDisableEditButton"
        @click="editCurrentWidget"
      >
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.EDIT') }}
      </woot-button>
      <woot-button variant="clear" @click="closeModal">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.CANCEL') }}
      </woot-button>
    </div>
  </woot-modal>
</template>

<style>
.message-editor {
  @apply px-3;

  ::v-deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}
</style>
